import React, {
    forwardRef, useCallback, useMemo
} from 'react';
import PropTypes from 'prop-types';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';

import { Tracking } from 'bdvp-tracking';

import Carousel from '../../Carousel';
import RowHeading from '../../RowHeading';
import { useImageUtils, useMedia } from '../../../hooks';
import {
    useTeamId,
    useIsClubPage,
} from '../../../hooks/config';
import {
    SEARCH_ROUTE, QUERY_PARAMS, CarouselRowType
} from '../../../utils/constants';
import { PARTIAL_VISIBILITY_GUTTER, LAYOUT_ITEMS } from './constants';
import Card from './Card';
import Grid from './Grid';

const carouselType = CarouselRowType.CATEGORY_V2;

const CategoryRow = forwardRef((props, ref) => {

    const {
        skip,
        properties,
        rank,
        className,
    } = props;

    const {
        title,
        categories = [],
    } = properties;

    const isSmallBreakpoint = useMedia([`(min-width: ${BREAKPOINTS.sm})`], [false], true);
    const teamId = useTeamId();
    const isClubPage = useIsClubPage();
    const { formatTemplateUrl } = useImageUtils();

    const data = useMemo(() => categories.map(({
        query, queryClubFilter, imageStoragePath, title: categoryTitle,
    }) => {

        if (isClubPage && queryClubFilter) {

            query = `${queryClubFilter} == [${teamId}] AND ${query}`;

        }

        return {
            imageTemplateUrl: formatTemplateUrl(imageStoragePath),
            title: categoryTitle,
            link: `${SEARCH_ROUTE}?${QUERY_PARAMS.query}=${query}`,
        };

    }), [
        categories,
        formatTemplateUrl,
        isClubPage,
        teamId,
    ]);

    const onAfterChangeHandler = useCallback((isScrollLeft) => {

        Tracking.trackCarouselScroll({
            carouselRank: rank,
            carouselTitle: title,
            isScrollLeft,
            carouselType,
        });

    }, [
        rank,
        title,
    ]);

    const onItemClickHandler = useCallback((event, category, index) => {

        Tracking.trackCarouselOnItemClick({
            carouselRank: rank,
            carouselTitle: title,
            index,
            id: category,
            carouselType,
        });

    }, [
        rank,
        title,
    ]);

    const renderItem = useCallback(({
        title: category, imageTemplateUrl, link,
    }, skip, index = 0) => (
        <Card
            loading={skip}
            index={index}
            category={category}
            imageTemplateUrl={imageTemplateUrl}
            link={link}
            onClick={onItemClickHandler}
        />
    ), [onItemClickHandler]);

    if (data.length === 0 && !skip) {

        return null;

    }

    return (
        <div ref={ref} className={className}>
            <RowHeading
                loading={skip}
                text={title}
                enableLeftRightMargin
            />
            {isSmallBreakpoint
                ? <Grid
                    isLoading={skip}
                    list={data}
                    onItemClickHandler={onItemClickHandler}
                />
                : <Carousel
                    isLoading={skip}
                    list={data}
                    renderItem={renderItem}
                    onAfterChange={onAfterChangeHandler}
                    partialVisibilityGutter={PARTIAL_VISIBILITY_GUTTER}
                    layoutItems={LAYOUT_ITEMS}
                />
            }
        </div>
    );

});

CategoryRow.propTypes = {
    rank: PropTypes.number.isRequired,
    skip: PropTypes.bool,
    properties: PropTypes.shape({
        categories: PropTypes.arrayOf(
            PropTypes.shape({
                imageStoragePath: PropTypes.string,
                query: PropTypes.string,
                queryClubFilter: PropTypes.string,
                title: PropTypes.string,
            })
        ),
        title: PropTypes.string,
    }),
    className: PropTypes.string,
};

CategoryRow.defaultProps = {
    skip: false,
    properties: {},
};

export default CategoryRow;
